import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, BookOpen, Image } from "lucide-react";
import { Story } from "./types";
import { StoryDialog } from "./StoryDialog";
import { supabase } from "@/integrations/supabase/client";

interface CategoryStoriesViewProps {
  memorialId: string;
  categoryId: string;
  categoryName: string;
  onBack: () => void;
}

export const CategoryStoriesView = React.memo<CategoryStoriesViewProps>(({ 
  memorialId, 
  categoryId, 
  categoryName, 
  onBack 
}) => {
  const [stories, setStories] = useState<Story[]>([]);
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch stories for this category
  useEffect(() => {
    const fetchStories = async () => {
      try {
        let query = supabase
          .from('stories')
          .select('*, media(*)')
          .eq('memorial_id', memorialId);

        // Handle special "other" category for uncategorized stories
        if (categoryId === 'other') {
          query = query.is('category', null);
        } else {
          query = query.eq('category', categoryId);
        }

        const { data, error } = await query.order('story_date', { ascending: false, nullsFirst: false });

        if (error) throw error;
        
        // Transform the data to match Story interface
        const transformedData = (data || []).map(story => ({
          ...story,
          media: (story.media || []).map(mediaItem => ({
            ...mediaItem,
            media_type: mediaItem.media_type as "image" | "video" | "audio"
          }))
        }));
        
        setStories(transformedData);
      } catch (error) {
        console.error('Error fetching category stories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (memorialId && categoryId) {
      fetchStories();
    }
  }, [memorialId, categoryId]);

  const handleStoryClick = (story: Story) => {
    setSelectedStory(story);
    setIsViewDialogOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "No date";
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-start">
          <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Categories
          </Button>
        </div>
        <div className="text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-2" />
            <div className="h-4 bg-gray-200 rounded w-24 mx-auto" />
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button section */}
      <div className="flex justify-start">
        <Button variant="ghost" size="sm" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Category title section */}
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-bold text-brand-text-primary">{categoryName}</h2>
        <p className="text-brand-secondary">{stories.length} {stories.length === 1 ? 'story' : 'stories'}</p>
      </div>

      {/* Stories list */}
      <div className="space-y-4">
        {stories.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <BookOpen className="h-12 w-12 text-brand-secondary mx-auto mb-4" />
              <h3 className="text-lg font-medium text-brand-text-primary mb-2">No stories in this category yet</h3>
              <p className="text-brand-secondary">
                No memories shared for {categoryName.toLowerCase()} yet
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {stories.map((story) => (
              <Card 
                key={story.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleStoryClick(story)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-brand-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-brand-text-primary truncate">
                          {story.title}
                        </h3>
                        <span className="text-sm text-brand-secondary flex-shrink-0 ml-4">
                          {formatDate(story.story_date)}
                        </span>
                      </div>
                      <p className="text-brand-secondary line-clamp-3 mb-3">
                        {story.content}
                      </p>
                      {story.media && story.media.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-brand-secondary">
                          <Image className="h-4 w-4" />
                          <span>{story.media.length} photo{story.media.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </div>

      {/* Story Dialog */}
      {selectedStory && (
        <StoryDialog
          story={selectedStory}
          isOpen={isViewDialogOpen}
          setIsOpen={setIsViewDialogOpen}
          isViewMode={true}
          setIsViewMode={() => {}}
          onUpdate={() => {}}
          onSave={() => Promise.resolve(false)}
          onDelete={() => Promise.resolve(false)}
          onMediaUpload={() => {}}
          onMediaRemove={() => {}}
          isSaving={false}
          readOnly={true}
        />
      )}
    </div>
  );
});

CategoryStoriesView.displayName = 'CategoryStoriesView';